import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { SupportSessionRefusal } from '../actions';
import { useT } from '../messages';
import type { Session } from '../wire';

/**
 * **Whether this seat may change the book** — one reading, for the four book
 * screens (plan, seventh pass L15).
 *
 * `Team`, `Orders`, `FrontDesk` and `Departures` each learned the same rule on
 * their own: a control is drawn for `session.can_write` and **hidden, not
 * disabled**, for anything else, because `core-api` answers `403` to a
 * `readonly` seat and to the console's support look on every `write: true`
 * route, and a button that opens a door that does not open is the thing not to
 * draw. The book screens — Today, the calendar, the booking sheet and the
 * appointment sheet — predate that rule and drew every button for every seat;
 * the tap was refused by the outbox with a typed error nobody rendered. This
 * file is the rule stated once so the fourth screen cannot learn a fifth
 * version of it.
 *
 * `can_write` is the server's word (`routes/session.ts`), not a reading of
 * `role`: it is already false for a support session, whose role is forced to
 * `readonly` anyway, and it is the one flag a future seat kind would flip.
 *
 * **Seeded from the synchronous snapshot**, for `CalendarView`'s reason: the
 * session arrives from IndexedDB a tick after the first render, and a screen
 * that hid its buttons for that tick and then drew them would flicker on every
 * open. `sessionNow()` is what the mirror already holds; the effect still
 * corrects a cold open, where the honest first paint is "nothing to press"
 * until the seat is known.
 */
export function useSeat(): { session: Session | null; canWrite: boolean } {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), runtime.mirror.sessionNow() ?? null);
  return { session, canWrite: session?.can_write === true };
}

/**
 * **The one sentence a refused write gets.**
 *
 * Two seats reach here, and they are told different things because they *are*
 * different things: the support look (`runtime.supportSession()`, the console's
 * fifteen-minute read-only session) is told it is a support view, in the same
 * words `actions.ts` puts on its `SupportSessionRefusal`; a real `readonly`
 * seat — a night auditor, a front-desk trainee — is told the seat reads and
 * does not write. Neither sentence blames the person, because neither person
 * did anything wrong: a door was drawn where there should have been a wall,
 * and this is the wall saying so.
 */
export function useSeatRefusal(): string {
  const runtime = useRuntime();
  const t = useT();
  return runtime.supportSession() === null
    ? t('actions.readOnlyRefused')
    : t('actions.supportRefused');
}

/**
 * Catch the outbox's typed refusal on a fire-and-forget write and keep its
 * sentence for the screen to render.
 *
 * The book screens call `void actions.setStatus(…)` from a button and never
 * look at the promise, so before this the `SupportSessionRefusal` the outbox
 * guard rejects with went to the console and nowhere else — the plan's "a
 * typed error nobody renders". `attempt` swallows exactly that one error type
 * and rethrows everything else, because a refusal is a sentence for the person
 * in front of the screen and any other failure is still a bug for the log.
 *
 * With the doors now hidden for a seat that cannot write, this fires only when
 * the two disagree — the mirrored session says the seat may write and the
 * credential says it is a support look — which is a state a stale mirror can
 * produce for one hydrate and a state the screen should still answer with a
 * sentence rather than silence.
 */
export function useWriteAttempt(): {
  refusal: string | null;
  attempt: (write: Promise<unknown>) => void;
  clear: () => void;
} {
  const [refusal, setRefusal] = useState<string | null>(null);
  const attempt = useCallback((write: Promise<unknown>): void => {
    write.then(
      () => setRefusal(null),
      (e: unknown) => {
        if (e instanceof SupportSessionRefusal) {
          setRefusal(e.message);
          return;
        }
        throw e;
      },
    );
  }, []);
  const clear = useCallback(() => setRefusal(null), []);
  return { refusal, attempt, clear };
}

/** The sentence, drawn where the button was. `null` draws nothing. */
export function WriteRefusal({ message }: { message: string | null }): ReactNode {
  if (message === null) return null;
  return (
    <p className="error" role="alert">
      {message}
    </p>
  );
}

/**
 * **May this seat change what the business sells?** (B10) — products,
 * services and their photographs.
 *
 * The server's word when it said one (`can_change_catalog`, projected from the
 * business's `catalog_editors` setting by the same function `core-api`'s route
 * gate calls), and B7's rule when it did not — the owner, on a seat that
 * writes — because a session mirrored before the field existed belongs to a
 * business nobody has asked, and `owner` is that business's answer. Never for
 * the console's support look, which writes nothing.
 */
export function canChangeCatalog(session: Session | null): boolean {
  if (session === null || !session.can_write || session.support !== undefined) return false;
  return session.can_change_catalog ?? session.user.role === 'owner';
}
